/**
 * 页面层消息队列。
 *
 * 位置上位于 API 流速控制队列之后、React 页面状态处理之前。
 * 它负责两件事：
 * 1. 控制页面真正消费事件的节奏（例如步骤切换时停留）
 * 2. 提供暂停 / 继续能力，便于在演示时冻结页面观察当前状态
 *
 * 额外约定：
 * - `getDelayMs` 用于“消费前停顿”，适合先预览 UI，再真正消费当前事件
 * - `getPostDelayMs` 用于“消费后停顿”，适合先消费当前事件，再阻塞后续事件一段时间
 *
 * 像“先展示第三步完成提示，停留 1.2 秒后再进入第四步”这类需求，
 * 应直接使用 `getPostDelayMs`，不要在具体业务组件里自行缓存后续消息，
 * 这样暂停 / 继续 / flush / reset 的行为才能始终由页面队列统一托管。
 */

export interface PageEventQueueOptions<T> {
  /** 真正把事件交给页面处理的回调 */
  onItem: (item: T) => void;
  /** 页面层最小消费间隔；用于避免恢复后瞬间把积压消息全部打到页面上 */
  minIntervalMs?: number;
  /** 某条消息在页面层是否需要消费前停留；返回 0 表示立即消费 */
  getDelayMs?: (item: T) => number;
  /** 进入停留前的预处理，例如先把当前步骤标记为 completed */
  onDelayStart?: (item: T) => void;
  /** 某条消息在页面层是否需要消费后停留；返回 0 表示立即放行后续事件 */
  getPostDelayMs?: (item: T) => number;
}

export class PageEventQueue<T> {
  /** 等待页面消费的事件 */
  private queue: T[] = [];
  /** 页面真正消费事件的回调 */
  private onItem: (item: T) => void;
  /** 每条消息的页面层停留时长计算器 */
  private getDelayMs?: (item: T) => number;
  /** 每条消息消费完成后的页面层停留时长计算器 */
  private getPostDelayMs?: (item: T) => number;
  /** 页面层最小消费间隔 */
  private minIntervalMs: number;
  /** 进入停留前的预处理 */
  private onDelayStart?: (item: T) => void;
  /** 当前延迟消费的定时器 */
  private timer: number | null = null;
  /** 当前处于停留中的消息；暂停时会被重新放回队头 */
  private delayedItem: T | null = null;
  /** 当前已消费、但仍在阻塞后续事件的消息 */
  private postDelayedItem: T | null = null;
  /** 是否处于暂停态 */
  private paused = false;
  /** 是否已经销毁 */
  private destroyed = false;
  /** flush 等待者 */
  private flushResolvers: Array<() => void> = [];
  /** 上一次真正交给页面消费事件的时间戳 */
  private lastConsumeTime = 0;

  constructor(options: PageEventQueueOptions<T>) {
    this.onItem = options.onItem;
    this.minIntervalMs = Math.max(0, options.minIntervalMs ?? 0);
    this.getDelayMs = options.getDelayMs;
    this.getPostDelayMs = options.getPostDelayMs;
    this.onDelayStart = options.onDelayStart;
  }

  /** 入队后由页面层自行决定何时真正处理。 */
  enqueue(item: T): void {
    if (this.destroyed) return;
    this.queue.push(item);
    this.drain();
  }

  /** 暂停页面消费；若当前正处于消费前停留中，则把该消息放回队头。 */
  pause(): void {
    if (this.destroyed || this.paused) return;
    this.paused = true;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.delayedItem !== null) {
      this.queue.unshift(this.delayedItem);
      this.delayedItem = null;
    }
  }

  /** 恢复页面消费。 */
  resume(): void {
    if (this.destroyed || !this.paused) return;
    this.paused = false;
    this.drain();
  }

  /** 等待页面层队列排空。 */
  flush(): Promise<void> {
    if (
      this.queue.length === 0 &&
      this.timer === null &&
      this.delayedItem === null &&
      this.postDelayedItem === null
    ) {
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      this.flushResolvers.push(resolve);
    });
  }

  /** 清空当前页面层队列，用于任务重跑或报错后的快速复位。 */
  reset(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.paused = false;
    this.queue = [];
    this.delayedItem = null;
    this.postDelayedItem = null;
    this.lastConsumeTime = 0;
    this.resolveFlushIfIdle();
  }

  /** 销毁队列实例。 */
  destroy(): void {
    this.destroyed = true;
    this.reset();
  }

  /** 核心消费循环：未暂停时持续取队头，直到遇到需要停留的消息。 */
  private drain(): void {
    if (this.destroyed || this.paused || this.timer !== null) return;

    if (this.postDelayedItem !== null) {
      const current = this.postDelayedItem;
      const postDelayMs = this.getPostDelayMs?.(current) ?? 0;
      if (postDelayMs > 0) {
        this.timer = window.setTimeout(() => {
          this.timer = null;
          this.postDelayedItem = null;
          this.drain();
        }, postDelayMs);
        return;
      }
      this.postDelayedItem = null;
    }

    while (this.queue.length > 0) {
      const item = this.queue.shift()!;
      const holdDelayMs = this.getDelayMs?.(item) ?? 0;
      const elapsed = Date.now() - this.lastConsumeTime;
      const intervalDelayMs = Math.max(0, this.minIntervalMs - elapsed);
      const delayMs = Math.max(holdDelayMs, intervalDelayMs);

      if (delayMs > 0) {
        if (holdDelayMs > 0) {
          this.onDelayStart?.(item);
        }
        this.delayedItem = item;
        this.timer = window.setTimeout(() => {
          this.timer = null;
          const current = this.delayedItem;
          this.delayedItem = null;
          if (!this.destroyed && current !== null) {
            this.consumeItem(current);
          }
          if (this.timer === null) {
            this.drain();
          }
        }, delayMs);
        return;
      }

      this.consumeItem(item);
      if (this.destroyed || this.paused || this.timer !== null) {
        return;
      }
    }

    this.resolveFlushIfIdle();
  }

  /** 真正消费一条消息；若命中 post-delay，则消费后先阻塞后续事件。 */
  private consumeItem(item: T): void {
    this.lastConsumeTime = Date.now();
    this.onItem(item);

    const postDelayMs = this.getPostDelayMs?.(item) ?? 0;
    if (postDelayMs > 0) {
      this.postDelayedItem = item;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.postDelayedItem = null;
        this.drain();
      }, postDelayMs);
    }
  }

  /** 队列完全静止时释放所有 flush 等待者。 */
  private resolveFlushIfIdle(): void {
    if (
      this.queue.length === 0 &&
      this.timer === null &&
      this.delayedItem === null &&
      this.postDelayedItem === null &&
      this.flushResolvers.length > 0
    ) {
      const resolvers = this.flushResolvers;
      this.flushResolvers = [];
      resolvers.forEach(resolve => resolve());
    }
  }
}
